import React, { useEffect, useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import {
  Box, Checkbox, CircularProgress, FormControlLabel, Grid, InputAdornment, TextField, Typography,
} from '@mui/material'
import { T } from '../components/PartnerSignups/onboardingDesign'
import {
  Badge, Card, CardTitle, FieldLabel, Notice, OnboardingPage, PageHeader, PrimaryButton,
} from '../components/PartnerSignups/onboardingUi'

/**
 * Super admin: the platform standard written onto every merchant application
 * (backend utils/signupStandards.js) — the transaction mix, accepted card
 * types and sales estimates. Merchants aren't asked these on the sign-up form.
 * Below it, the agreement's Schedule A fees (utils/agreementFees.js), saved
 * separately. Styled like the merchant sign-up form (onboardingUi).
 */

const GET = gql`
  query MerchantSignupStandards {
    merchantSignupStandards { mixInPerson mixMobile mixEcommerce mixMoto visa mastercard discover amex monthlySales highTicket avgTicket isDefault updatedAt }
  }
`
const SAVE = gql`
  mutation SaveMerchantSignupStandards($input: MerchantSignupStandardsInput!) {
    saveMerchantSignupStandards(input: $input) { mixInPerson mixMobile mixEcommerce mixMoto visa mastercard discover amex monthlySales highTicket avgTicket isDefault updatedAt }
  }
`

const MIX = [
  ['mixInPerson', 'In person'],
  ['mixMobile', 'Mobile'],
  ['mixEcommerce', 'eCommerce'],
  ['mixMoto', 'Phone / mail (MOTO)'],
]
const SALES = [
  ['monthlySales', 'Monthly card sales'],
  ['highTicket', 'Largest single order'],
  ['avgTicket', 'Average order'],
]
const BRANDS = [
  ['visa', 'Visa'],
  ['mastercard', 'MasterCard'],
  ['discover', 'Discover'],
  ['amex', 'American Express'],
]

// Schedule A of the merchant agreement (backend utils/agreementFees.js).
const FEE_FIELDS = 'deliveryFeeMin minimumOrder returnedPaymentFee processingPercent processingFixed statementFee deliveryCommissionRate selfDeliveryCommissionRate pickupCommissionRate thirdPartyPercent serviceFee isDefault updatedAt'
const GET_FEES = gql`
  query MerchantAgreementFees {
    merchantAgreementFees { ${FEE_FIELDS} }
  }
`
const SAVE_FEES = gql`
  mutation SaveMerchantAgreementFees($input: MerchantAgreementFeesInput!) {
    saveMerchantAgreementFees(input: $input) { ${FEE_FIELDS} }
  }
`
const ORDER_PERCENT = [
  ['deliveryCommissionRate', 'Delivery orders', 'RushMyFood delivers'],
  ['selfDeliveryCommissionRate', 'Self-delivery orders', "The restaurant's own driver delivers"],
  ['pickupCommissionRate', 'Pickup orders', 'The customer picks up'],
]
const FEE_MONEY = [
  ['deliveryFeeMin', 'Delivery fee (minimum)'],
  ['minimumOrder', 'Minimum order amount'],
  ['returnedPaymentFee', 'Returned payment fee'],
  ['statementFee', 'Monthly processor statement / account fee'],
]
const FEE_KEYS = [...ORDER_PERCENT.map(([k]) => k), ...FEE_MONEY.map(([k]) => k), 'processingPercent', 'processingFixed', 'thirdPartyPercent']

const amount = v => Number(String(v).replace(/[$,%\s]/g, ''))

// The page's width: MUI's md container, as before the restyle.
const PAGE_WIDTH = 900
// A grid cell whose label sits right above its input, so a row's inputs line
// up when one label wraps onto two lines.
const fieldCell = { display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }
// The line under a card or section title (its color follows validation).
const lead = { fontSize: 14, lineHeight: 1.5 }
// A section heading inside a card.
const sectionTitle = { fontWeight: 600, fontSize: 15, color: T.text, mb: 0.5 }
const divider = { borderTop: `1px solid ${T.divider}` }
const savedAt = { color: T.hint, fontSize: 13, textAlign: 'right' }
// A card brand: a bordered tile, tinted while it's ticked.
const brandTile = {
  m: 0, pl: 0.5, pr: 2, border: `1px solid ${T.input}`, borderRadius: '12px', background: T.card,
  '& .MuiFormControlLabel-label': { fontSize: 15, fontWeight: 500, color: T.text },
  '&:hover': { borderColor: T.inputHover },
  '&:has(.Mui-checked)': { borderColor: T.red, background: T.redTint },
}

function AgreementFees() {
  const { data, loading, error } = useQuery(GET_FEES, { fetchPolicy: 'network-only' })
  const [save, { loading: saving }] = useMutation(SAVE_FEES)
  const [f, setF] = useState(null)
  const [notice, setNotice] = useState(null)
  const [saveError, setSaveError] = useState(null)
  const current = data && data.merchantAgreementFees

  useEffect(() => {
    if (current && !f) setF(Object.fromEntries(FEE_KEYS.map(k => [k, String(current[k] ?? '')])))
  }, [current, f])

  if (error) return <Notice severity="error" sx={{ mb: 3 }}>{error.message}</Notice>
  if (loading || !f) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box>

  const percentOk = k => f[k] !== '' && amount(f[k]) >= 0 && amount(f[k]) <= 100
  const moneyOk = k => f[k] !== '' && amount(f[k]) >= 0 && amount(f[k]) <= 10000
  const ratesOk = ORDER_PERCENT.every(([k]) => percentOk(k))
  const valid = ratesOk && percentOk('processingPercent') && percentOk('thirdPartyPercent') && [...FEE_MONEY.map(([k]) => k), 'processingFixed'].every(moneyOk)
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }))
  const dollars = { startAdornment: <InputAdornment position="start">$</InputAdornment> }
  const percent = { endAdornment: <InputAdornment position="end">%</InputAdornment> }

  const submit = async () => {
    setNotice(null)
    setSaveError(null)
    try {
      await save({
        variables: { input: Object.fromEntries(FEE_KEYS.map(k => [k, amount(f[k])])) },
        refetchQueries: ['MerchantAgreementFees'],
      })
      setNotice('Saved. Agreements signed from now on show these fees, and the order percentages are now the platform default commission.')
    } catch (e) {
      setSaveError(e.message)
    }
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardTitle
        sub="RushMyFood's fees, as the merchant sees and signs them in the agreement."
        action={current.isDefault
          ? <Badge>Using the default</Badge>
          : <Typography sx={savedAt}>Last saved {new Date(current.updatedAt).toLocaleString()}</Typography>}
      >
        Agreement fees (Schedule A)
      </CardTitle>
      {notice && <Notice severity="success" onClose={() => setNotice(null)}>{notice}</Notice>}
      {saveError && <Notice severity="error" onClose={() => setSaveError(null)}>{saveError}</Notice>}

      <Box sx={{ ...divider, pt: 2.5 }}>
        <Typography sx={sectionTitle}>Order percentage (% of subtotal)</Typography>
        <Typography sx={{ ...lead, color: ratesOk ? T.hint : T.error, mb: 2 }}>
          {ratesOk
            ? 'Linked to commission: these are the platform default commission rates (also on Configuration > Platform Fees). A restaurant approved through sign-up starts on the rates it signed, and they can be changed for it on Commission Rates.'
            : 'Each order percentage must be from 0 to 100.'}
        </Typography>
        <Grid container spacing={2}>
          {ORDER_PERCENT.map(([k, label, hint]) => (
            <Grid item xs={12} sm={4} key={k}>
              <FieldLabel htmlFor={`fee-${k}`} label={label} />
              <TextField id={`fee-${k}`} fullWidth size="small" helperText={hint} value={f[k]} onChange={set(k)}
                inputProps={{ inputMode: 'decimal' }} InputProps={percent} />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ ...divider, mt: 3, pt: 2.5 }}>
        <Typography sx={sectionTitle}>Third-party orders and service fee</Typography>
        <Typography sx={{ ...lead, color: T.hint, mb: 2 }}>
          Third-party orders: the % of the subtotal RushMyFood charges, with the delivery fee and tip, on Grubhub, Uber Eats and DoorDash orders our drivers deliver.
          The service fee is what customers pay on every order; it is the platform service fee and is changed where it always was, so it is shown here only.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} sx={fieldCell}>
            <FieldLabel htmlFor="fee-thirdPartyPercent" label="Third-party orders we deliver" />
            <TextField id="fee-thirdPartyPercent" fullWidth size="small" value={f.thirdPartyPercent} onChange={set('thirdPartyPercent')}
              error={!percentOk('thirdPartyPercent')} inputProps={{ inputMode: 'decimal' }} InputProps={percent} helperText="% of the subtotal" />
          </Grid>
          <Grid item xs={12} sm={6} sx={fieldCell}>
            <FieldLabel htmlFor="fee-serviceFee" label="Service fee (customers pay, per order)" />
            <TextField id="fee-serviceFee" fullWidth size="small" value={current.serviceFee == null ? '' : Number(current.serviceFee).toFixed(2)} disabled
              InputProps={dollars} helperText="The platform service fee" />
          </Grid>
        </Grid>
        <Typography sx={{ color: T.hint, fontSize: 13, lineHeight: 1.5, mt: 2 }}>
          The order percentages, third-party % and service fee are minimums: agents and ISOs may set a restaurant’s higher on their own, and earn a share of the difference. Lower needs your approval.
        </Typography>
      </Box>

      <Box sx={{ ...divider, mt: 3, pt: 2.5 }}>
        <Typography sx={{ ...sectionTitle, mb: 2 }}>Other fees</Typography>
        <Grid container spacing={2}>
          {FEE_MONEY.map(([k, label]) => (
            <Grid item xs={12} sm={6} key={k} sx={fieldCell}>
              <FieldLabel htmlFor={`fee-${k}`} label={label} />
              <TextField id={`fee-${k}`} fullWidth size="small" value={f[k]} onChange={set(k)} error={!moneyOk(k)}
                inputProps={{ inputMode: 'decimal' }} InputProps={dollars} />
            </Grid>
          ))}
          <Grid item xs={6} sm={3} sx={fieldCell}>
            <FieldLabel htmlFor="fee-processingPercent" label="Payment processing" />
            <TextField id="fee-processingPercent" fullWidth size="small" value={f.processingPercent} onChange={set('processingPercent')}
              error={!percentOk('processingPercent')} inputProps={{ inputMode: 'decimal' }} InputProps={percent} />
          </Grid>
          <Grid item xs={6} sm={3} sx={fieldCell}>
            <FieldLabel htmlFor="fee-processingFixed" label="+ per transaction" />
            <TextField id="fee-processingFixed" fullWidth size="small" value={f.processingFixed} onChange={set('processingFixed')}
              error={!moneyOk('processingFixed')} inputProps={{ inputMode: 'decimal' }} InputProps={dollars} />
          </Grid>
        </Grid>
        <Typography sx={{ color: T.hint, fontSize: 13, lineHeight: 1.5, mt: 2 }}>
          The payment processing rate also fills the discount rate and auth fee in the processor fee disclosure.
          Each signed agreement keeps the fees it was signed with.
        </Typography>
      </Box>

      <Box sx={{ ...divider, display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2.5 }}>
        <PrimaryButton onClick={submit} disabled={saving || !valid} sx={{ px: 4 }}>
          {saving ? 'Saving…' : 'Save fees'}
        </PrimaryButton>
      </Box>
    </Card>
  )
}

export default function SignupStandards() {
  const { data, loading, error } = useQuery(GET, { fetchPolicy: 'network-only' })
  const [save, { loading: saving }] = useMutation(SAVE)
  const [f, setF] = useState(null)
  const [notice, setNotice] = useState(null)
  const [saveError, setSaveError] = useState(null)
  const current = data && data.merchantSignupStandards

  useEffect(() => {
    if (current && !f) {
      setF({
        mixInPerson: String(current.mixInPerson),
        mixMobile: String(current.mixMobile),
        mixEcommerce: String(current.mixEcommerce),
        mixMoto: String(current.mixMoto),
        visa: current.visa,
        mastercard: current.mastercard,
        discover: current.discover,
        amex: current.amex,
        monthlySales: String(current.monthlySales ?? ''),
        highTicket: String(current.highTicket ?? ''),
        avgTicket: String(current.avgTicket ?? ''),
      })
    }
  }, [current, f])

  if (loading || (!f && !error)) return <OnboardingPage maxWidth={PAGE_WIDTH}><Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box></OnboardingPage>
  if (error) return <OnboardingPage maxWidth={PAGE_WIDTH}><Notice severity="error">{error.message}</Notice></OnboardingPage>

  const total = MIX.reduce((s, [k]) => s + (Number(f[k]) || 0), 0)
  const anyCard = BRANDS.some(([k]) => f[k])
  const money = v => Number(String(v).replace(/[$,\s]/g, ''))
  const salesOk = SALES.every(([k]) => money(f[k]) > 0)
  const avgOk = !salesOk || money(f.avgTicket) <= money(f.highTicket)
  const valid = total === 100 && anyCard && salesOk && avgOk && MIX.every(([k]) => Number.isInteger(Number(f[k])) && f[k] !== '')

  const submit = async () => {
    setNotice(null)
    setSaveError(null)
    try {
      await save({
        variables: {
          input: {
            ...Object.fromEntries(MIX.map(([k]) => [k, Number(f[k])])),
            ...Object.fromEntries(BRANDS.map(([k]) => [k, !!f[k]])),
            ...Object.fromEntries(SALES.map(([k]) => [k, money(f[k])])),
          },
        },
        refetchQueries: ['MerchantSignupStandards'],
      })
      setNotice('Saved. New and in-progress applications use this from now on.')
    } catch (e) {
      setSaveError(e.message)
    }
  }

  return (
    <OnboardingPage maxWidth={PAGE_WIDTH}>
      <PageHeader
        title="Signup Standards"
        subtitle="Card processing details applied to every merchant application (merchants are not asked these), and the fees in the merchant agreement."
      />

      {notice && <Notice severity="success" onClose={() => setNotice(null)}>{notice}</Notice>}
      {saveError && <Notice severity="error" onClose={() => setSaveError(null)}>{saveError}</Notice>}

      <Card sx={{ mb: 2 }}>
        <CardTitle
          sx={{ mb: 0.5 }}
          action={current.isDefault
            ? <Badge>Using the default</Badge>
            : <Typography sx={savedAt}>Last saved {new Date(current.updatedAt).toLocaleString()}</Typography>}
        >
          Transaction mix
        </CardTitle>
        <Typography sx={{ ...lead, color: total === 100 ? T.green : T.error, mb: 2 }}>
          How merchants take card payments, by percentage. Must add up to 100%. Now: {total}%
        </Typography>
        <Grid container spacing={2}>
          {MIX.map(([k, label]) => (
            <Grid item xs={6} sm={3} key={k} sx={fieldCell}>
              <FieldLabel htmlFor={`std-${k}`} label={label} />
              <TextField
                id={`std-${k}`} fullWidth size="small" type="number" value={f[k]}
                onChange={e => setF(x => ({ ...x, [k]: e.target.value }))}
                inputProps={{ min: 0, max: 100, step: 1 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>
          ))}
        </Grid>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardTitle sx={{ mb: 0.5 }}>Accepted card types</CardTitle>
        <Typography sx={{ ...lead, color: anyCard ? T.hint : T.error, mb: 2 }}>
          {anyCard ? 'The card brands every merchant accepts.' : 'Choose at least one card brand.'}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {BRANDS.map(([k, label]) => (
            <FormControlLabel
              key={k}
              sx={brandTile}
              control={<Checkbox checked={!!f[k]} onChange={e => setF(x => ({ ...x, [k]: e.target.checked }))} />}
              label={label}
            />
          ))}
        </Box>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardTitle sx={{ mb: 0.5 }}>Sales estimates</CardTitle>
        <Typography sx={{ ...lead, color: salesOk && avgOk ? T.hint : T.error, mb: 2 }}>
          {!salesOk
            ? 'Enter each amount, above zero.'
            : !avgOk
              ? 'The average order cannot be more than the largest order.'
              : 'Sent to the payment processor with every merchant application.'}
        </Typography>
        <Grid container spacing={2}>
          {SALES.map(([k, label]) => (
            <Grid item xs={12} sm={4} key={k} sx={fieldCell}>
              <FieldLabel htmlFor={`std-${k}`} label={label} />
              <TextField
                id={`std-${k}`} fullWidth size="small" value={f[k]}
                onChange={e => setF(x => ({ ...x, [k]: e.target.value }))}
                inputProps={{ inputMode: 'decimal' }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
          ))}
        </Grid>
      </Card>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 4 }}>
        <Typography sx={{ color: T.hint, fontSize: 13, lineHeight: 1.5, flex: '1 1 260px' }}>
          Each application keeps the standard that was in effect when it was signed.
        </Typography>
        <PrimaryButton onClick={submit} disabled={saving || !valid} sx={{ px: 4, ml: 'auto' }}>
          {saving ? 'Saving…' : 'Save standards'}
        </PrimaryButton>
      </Box>

      <AgreementFees />
    </OnboardingPage>
  )
}
